import React, { useState } from "react";
import Masonry from "react-masonry-css";
import "./Library.css";
import {
  IconButton,
  Box,
  Drawer,
  Typography,
  TextField,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getLibraryDetails , getLibraryEventDetails , getLibraryFilterShowcase, getLibraryFilterShowcaseVariation} from "../../services/apiService";
import LoaderWithBackdrop from "../Loader/loader";
import CloseIcon from "@mui/icons-material/Close";

interface LibraryProps {
  displayType?: "popup" | "page";
  onClose?: () => void;
  onSelectImage?: (url: string) => void;
  setImage?: (value: string | null) => void;
}

function Library({ onClose, onSelectImage, setImage }: LibraryProps) {
  const [open, setOpen] = useState(true);
  const [popupItem, setPopupItem] = useState<any>(null);
  const [checked, setChecked] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const userId = localStorage.getItem("userId") || "";
  const [items, setItems]: [any[], React.Dispatch<React.SetStateAction<any[]>>] = React.useState<any[]>([]);
  const [allItems, setAllItems]: [any[], React.Dispatch<React.SetStateAction<any[]>>] = React.useState<any[]>([]);

  // Move categories above all useState calls that depend on it
  const categories = [
    {
      label: "All",
      value: "all",
      subcategories: [],
    },
    {
      label: "All Uploaded Images",
      value: "uploaded",
      subcategories: [
        { label: "Personas", value: "showcase" },
        // { label: "AI Influencer-Character", value: "aiInfluencer" },
        // { label: "AI Influencer-Variation", value: "aiVariation" },
        { label: "Cinemotion", value: "studio" },
        { label: "Podcast", value: "podcast" },
         { label: "Photo Talks", value: "photoTalks" },
        { label: "Cartoon", value: "cartoon" },
        { label: 'Ramp Walk', value: 'rampwalk' },
      ],
    },
    {
      label: "All Generated Videos",
      value: "videos",
      subcategories: [
        { label: "Cinemotion", value: "studio" },
        { label: "Podcast", value: "podcast" },
        { label: "Photo Talks", value: "photoTalks" },
        { label: 'Ramp Walk', value: 'rampwalk' },
        { label: 'Reels', value: 'reels' },
      ],
    },
    {
      label: "All Generated Images",
      value: "images",
      subcategories: [
        { label: "Personas", value: "showcase" },
        // { label: "AI Influencer-Character", value: "aiInfluencer" },
        // { label: "AI Influencer-Variation", value: "aiVariation" },
        { label: "Cartoon", value: "cartoon" },
        { label: 'Reels', value: 'reels' },

      ],
    },
  ];
  const [visibleCategories, setVisibleCategories] = useState(categories);

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1,
  };

  const isVideo = (url: string) =>
    typeof url === "string" && url.match(/\.(mp4|webm|ogg)$/i);
  const drawerWidth = open ? 300 : 60;

  const videoRefs = React.useRef<(HTMLVideoElement | null)[]>([]);

  const handleItemClick = (item: any, idx?: number) => {
    // Always pause all grid videos before opening popup
    videoRefs.current.forEach(video => {
      if (video && !video.paused) video.pause();
    });
    setPopupItem(item);
    if (item.type === "image") {
      setImage?.(item.url);
      if (onClose) onClose();
    }
    if (item.type === "video") {
      onSelectImage?.(item.url);
      if (onClose) onClose();
    }
  };

  React.useEffect(() => {
    // On mount, always fetch all data
    (async () => {
      setIsLoading(true);
      try {
        const res = await getLibraryDetails(userId);
        setItems(res.data.data || []);
        setAllItems(res.data.data || []);
        setChecked((prev) => ({ ...prev, all: true }));
      } catch (err) {
        console.error("getLibraryDetails error:", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [userId]);

    const studioId = '67ed04b99104e4df3945139c';
  const cartoonId = localStorage.getItem("cartoonModelId") || "68146db837ac9f090a4ca50a";
  const characteristicsId = localStorage.getItem("aiInfluencerCharacterID") || "683422b3227c23ad8919e8be";
  const variationId = localStorage.getItem("aiVariationID") || "6834224e227c23ad8919e8bd";
  const photoTalksId = localStorage.getItem("photoTalksModelId") || "684fc10aae6234d0dcf96cde";
  const rampwalkId = localStorage.getItem("rampEventID") || "685e428e94e0f8921c36a887";
  const reelsId = localStorage.getItem("audioEventId") || "68590b5ec4e2a96de6be37dc";

  // Helper to get composite key
  const getCategoryKey = (parent: string, sub?: string) => sub ? `${parent}-${sub}` : parent;

  const handleCategoryChange = async (value: string, parent?: string) => {
    // Use composite key for subcategories
    const key = parent ? getCategoryKey(parent, value) : value;
    setChecked({ [key]: true });
    setIsLoading(true);
    // Map subcategory to eventID
    const eventMap: { [key: string]: string } = {
      cartoon: cartoonId,
      studio: studioId,
      podcast: '6812186837ac9f090a4ca4fd',
      showcase: "",
      photoTalks: photoTalksId,
      rampwalk: rampwalkId,
      reels: reelsId,
    };
    // Find parent category for subcategory
    const parentCategory = parent
      ? categories.find(cat => cat.value === parent)
      : categories.find(cat => cat.subcategories.some(sub => sub.value === value));
    if (parent && eventMap[value] && parentCategory) {
      let filterType = '';
      if (parentCategory.value === 'uploaded') {
        filterType = 'uploaded image';
      } else if (parentCategory.value === 'images') {
        filterType = 'generated image';
      } else if (parentCategory.value === 'videos') {
        filterType = 'generated video';
      }
      try {
        const res = await getLibraryEventDetails(userId, eventMap[value]);
        const filtered = (res.data.data || []).filter((item: any) => item.type === filterType);
        setItems(filtered);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error("getLibraryEventDetails error:", err);
      }
      return;
    }
    // Special handling for 'showcase'
if (
  parent &&
  (
    value === 'showcase' ||
    value === 'aiInfluencer' ||
    value === 'aiVariation'
  )
) {
  let filterType = '';
  if (parentCategory?.value === 'uploaded') {
    filterType = 'uploaded image';
  } else if (parentCategory?.value === 'images') {
    filterType = 'generated image';
  }
  try {
    const typeParam = parentCategory?.value === 'uploaded' ? 'uploads' : 'images';
    let eventId = "";
    if (parent && value === 'aiVariation') {
      eventId = variationId;
    } else if (parent && value === 'aiInfluencer') {
      eventId = characteristicsId;
    } else if (parent && value === 'showcase') {
      eventId = "";
    } else {
      eventId = eventMap[value];
    }
    
    // Call getLibraryFilterShowcase if typeParams is 'uploads', otherwise call getLibraryFilterShowcaseVariation with an array of eventIds.
if (typeParam === "uploads") {
      const showcaseRes = await getLibraryFilterShowcase(userId, typeParam, eventId);
      const filtered = (showcaseRes.data.data || []).filter((item: any) => item.type === filterType);
      setItems(filtered);
      setIsLoading(false);
    } else {
      const showcaseRes = await getLibraryFilterShowcaseVariation(userId, typeParam, [variationId, characteristicsId, eventId]);
      const filtered = (showcaseRes.data.data || []).filter((item: any) => item.type === filterType);
      setItems(filtered);
      setIsLoading(false);
    }
  } catch (err) {
    setIsLoading(false);
    console.error("getLibraryFilterShowcase error:", err);
  }
  return;
}
    // For event-based subcategories
    const eventMapFallback: { [key: string]: string } = {
      cartoon: cartoonId,
      
    };
    if (value === "all") {
      try {
        const res = await getLibraryDetails(userId);
        setIsLoading(false);
        setItems(res.data.data || []);
        setAllItems(res.data.data || []);
      } catch (err) {
        setIsLoading(false);
        console.error("getLibraryDetails error:", err);
      }
    } else if (value === "uploaded") {
      setItems(allItems.filter((item: any) => item.type === "uploaded image"));
      setIsLoading(false);
    } else if (value === "images") {
      setItems(allItems.filter((item: any) => item.type === "generated image"));
      setIsLoading(false);
    } else if (value === "videos") {
      setItems(allItems.filter((item: any) => item.type === "generated video"));
      setIsLoading(false);
    } else if (eventMapFallback[value]) {
      try {
        const res = await getLibraryEventDetails(userId, eventMapFallback[value]);
        const filtered = (res.data.data || []).filter((item: any) => item.type === "generated image");
        setItems(filtered);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error("getLibraryEventDetails error:", err);
      }
    } else {
      setIsLoading(false);
    }
  };

  // At the top of the component, add a state to track mobile drawer
const isMobile = window.innerWidth <= 600;
const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Helper to detect media type
  const getMediaType = (url: string) => {
    if (typeof url !== "string") return "unknown";
    if (url.match(/\.(mp4|webm|ogg)$/i)) return "video";
    if (url.match(/\.(mp3|wav|ogg)$/i)) return "audio";
    if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) return "image";
    return "unknown";
  };
     



  // Main UI for page only
  return (
    <Box>
       {isLoading && <LoaderWithBackdrop open={isLoading} />} 
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        
        <Typography variant="h1" gutterBottom className="studioNavHeading">
          My Library
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: 'white', mr: 1 }}>
          <img src={drawerWidth === 300 ? '/assets/menu_collapsed.svg' : '/assets/MenuIcon.svg'} alt="Menu" className="menuIcon"/>
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: "flex" }} pb={4} pl={4} pr={4}>
        {/* Left Drawer */}
        {isMobile ? (
          <>
            <Drawer
              variant="temporary"
              anchor="left"
              open={mobileDrawerOpen}
              onClose={() => setMobileDrawerOpen(false)}
              className="custom-drawer"
              sx={{ width: 260, zIndex: 1302 }}
            >
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: "white" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <FormGroup>
                {visibleCategories.map((category) => (
                  <Box key={category.value} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!checked[category.value]}
                          onChange={() => handleCategoryChange(category.value)}
                          sx={{ color: "white" }}
                        />
                      }
                      label={<Typography color="white">{category.label}</Typography>}
                    />
                    {category.subcategories.length > 0 && (
                      <Box pl={4} sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                        {category.subcategories.map((sub) => (
                          <FormControlLabel
                            key={sub.value}
                            control={
                              <Checkbox
                                checked={!!checked[getCategoryKey(category.value, sub.value)]}
                                onChange={() => handleCategoryChange(sub.value, category.value)}
                                sx={{ color: "white" }}
                              />
                            }
                            label={<Typography color="white">{sub.label}</Typography>}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </FormGroup>
            </Drawer>
          </>
        ) : (
          <Drawer
            variant="permanent"
            anchor="left"
            className="custom-drawer"
            sx={{
              width: drawerWidth,
            }}
          >
            <Box display="flex" justifyContent="flex-end" mb={2}>
              {open && (
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search Images, videos..."
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    if (!value) {
                      setVisibleCategories(categories);
                      return;
                    }
                    const filtered = categories
                      .map((category) => {
                        // Check if category label matches
                        if (category.label.toLowerCase().includes(value.toLowerCase())) {
                          return category;
                        }
                        // Filter subcategories
                        const subcategories = category.subcategories.filter((sub) =>
                          sub.label.toLowerCase().includes(value.toLowerCase())
                        );
                        if (subcategories.length > 0) {
                          return { ...category, subcategories };
                        }
                        return null;
                      })
                      .filter(Boolean);
                  setVisibleCategories(filtered as typeof categories);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className="search-icon" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <CloseIcon
                        onClick={() => {
                          setSearchTerm('');
                          setVisibleCategories(categories);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              )}
              <IconButton onClick={() => setOpen(!open)} sx={{ color: "white" }}>
                <img src={drawerWidth === 300 ? '/assets/menu_collapsed.svg' : '/assets/MenuIcon.svg'} alt="Menu" className="menuIcon"/>
              </IconButton>
            </Box>
            {open && (
              <FormGroup>
                {visibleCategories.map((category) => (
                  <Box key={category.value} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!checked[category.value]}
                          onChange={() => handleCategoryChange(category.value)}
                          sx={{ color: "white" }}
                        />
                      }
                      label={
                        <Typography color="white">{category.label}</Typography>
                      }
                    />
                    {category.subcategories.length > 0 && (
                      <Box
                        pl={4}
                        sx={{ display: "flex", flexDirection: "column", gap: 0 }}
                      >
                        {category.subcategories.map((sub) => (
                          <FormControlLabel
                            key={sub.value}
                            control={
                              <Checkbox
                                checked={!!checked[getCategoryKey(category.value, sub.value)]}
                                onChange={() => handleCategoryChange(sub.value, category.value)}
                                sx={{ color: "white" }}
                              />
                            }
                            label={<Typography color="white">{sub.label}</Typography>}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </FormGroup>
            )}
          </Drawer>
        )}
        {/* Right Masonry Grid */}
        <Box
  component="main"
  // sx={{ flexGrow: 1,  overflowY: "auto" }}
  // padding= {{xs: 1, sm: 2, md: 2}}
>
  {items.length > 0 ? (
    <Masonry
    breakpointCols={breakpointColumnsObj}
    className="my-masonry-grid"
    columnClassName="my-masonry-grid_column"
  >
    {items.length > 0 &&
      items.filter((item: any) => typeof item.url === 'string' && item.url.startsWith('https://'))
        .map((item: any, idx: number) => {
          const mediaType = getMediaType(item.url);
          return (
            <div
              key={item.url + idx}
              className="masonry-item"
              style={{ 
                width: "100%", 
                cursor: "pointer", 
                position: "relative",
              }}
              onClick={() => handleItemClick(item, idx)}
            >
              {mediaType === "video" ? (
                <video
                  ref={el => videoRefs.current[idx] = el}
                  controls={!popupItem}
                  style={{ width: "100%", borderRadius: 8 }}
                  muted
                >
                  <source src={item.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : mediaType === "audio" ? (
                <audio
                  controls
                  style={{ width: "100%", borderRadius: 8 }}
                >
                  <source src={item.url} type="audio/mpeg" />
                  Your browser does not support the audio tag.
                </audio>
              ) : (
                <img
                  src={item.url}
                  alt={item.type}
                  style={{ width: "100%", borderRadius: 8 }}
                />
              )}
              <Box >
              </Box>
            </div>
          );
        })}
  </Masonry>
  ):(
    <Box sx={{ textAlign: "center", mt: 4 }}>
      <Typography variant="h6"  className="studioNavHeading">
        No Library Items Found
      </Typography>
    </Box>
  )}
        </Box>
        {/* Popup for item preview */}
        {popupItem && (
          <div
           className="popup-overlay"
            onClick={() => setPopupItem(null)}
          >
            <div
             className="popup-content"
              onClick={e => e.stopPropagation()}
            >
              <IconButton
                onClick={() => setPopupItem(null)}
                className="close-button"
              >
                <CloseIcon />
              </IconButton>
              {isVideo(popupItem.url) ? (
                <video
                  src={popupItem.url}
                  controls
                  className="video-content"
                  autoPlay
                />
              ) : (
                <img
                  src={popupItem.url}
                  alt={popupItem.type}
                  className="video-content"
                />
              )}
            </div>
          </div>
        )}
      </Box>
    </Box>
  );
}

export default Library;
